import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    const raw = this.refs.cartBubbleCount.textContent ?? '';
    const n = parseInt(raw.trim(), 10);
    return Number.isNaN(n) ? 0 : n;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    const countEl = this.refs.cartBubbleCount;
    if (countEl?.textContent && countEl.textContent !== countEl.textContent.trim()) {
      countEl.textContent = countEl.textContent.trim();
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Product-form sends qty delta; cart-items sends absolute total. Drive UI from the resolved total.
    const next = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', next === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', next === 0);
    this.currentCartCount = next;
    this.classList.toggle('header-actions__cart-icon--has-cart', next > 0);
    this.#syncCartDrawerEmptyState(next);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(next),
        timestamp: Date.now(),
      })
    );

    if (!animate || next === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Keeps cart drawer dialog empty-state class in sync when section morph uses hydration
   * (only cart-drawer-inner updates; dialog attributes are otherwise stale).
   *
   * @param {number} totalCount
   */
  #syncCartDrawerEmptyState(totalCount) {
    const dialog = this.closest('cart-drawer-component')?.querySelector('.cart-drawer__dialog');
    if (!dialog) return;

    const isEmpty = totalCount <= 0;
    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
  }

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent.trim();

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      const sessionCount = parseInt(value, 10);
      const domCount = parseInt(visibleCount, 10) || 0;

      // Liquid/DOM empty is source of truth — never revive a stale (1) over an empty cart.
      if (domCount === 0 && sessionCount > 0) {
        sessionStorage.removeItem('cart-count');
        return;
      }

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000 && sessionCount >= 0) {
        this.renderCartBubble(sessionCount, false, false);
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
